import React, { FunctionComponent, useMemo } from "react";
import { useIntl } from "react-intl";
import {
  MdOutlineError,
  MdOutlineInfo,
  MdOutlineQuestionMark,
  MdOutlineWarning,
  MdError,
  MdInfo,
  MdQuestionMark,
  MdWarning,
} from "react-icons/md";
import {
  Button,
  Icon,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

import { SiteAlarmTypes_siteAlarmTypes } from "graphql/generated";


interface IProps {
  item: SiteAlarmTypes_siteAlarmTypes;
  timezone: string;
  setDetailedType: (alarm_type: SiteAlarmTypes_siteAlarmTypes) => void;
}


const AlarmType: FunctionComponent<IProps> = ({
  item,
  timezone,
  setDetailedType,
}) => {
  const intl = useIntl();
  const textColor = useColorModeValue("white", "gray.800");
  const colorTint = useColorModeValue(500, 200);
  const lookActive = useMemo<boolean>(() => !item.muted && item.active && !item.ack, [item.active, item.muted, item.ack]);
  const lookMuted = useMemo<boolean>(() => item.muted, [item.muted]);
  const lookUnAcked = useMemo<boolean>(() => !item.muted && !item.active && !item.ack, [item.active, item.muted, item.ack]);
  const itemLevelColor = useMemo(() => {
    switch(item.level) {
      case "ERROR": return "red";
      case "WARNING": return "yellow";
      case "INFO": return "blue";
      default: return "black";
    }
  }, [item.level]);
  const itemLevelIcon = useMemo(() => {
    switch(item.level) {
      case "ERROR": return [MdError, MdOutlineError];
      case "WARNING": return [MdWarning, MdOutlineWarning];
      case "INFO": return [MdInfo, MdOutlineInfo];
      default: return [MdQuestionMark, MdOutlineQuestionMark];
    }
  }, [item.level]);
  const itemLevelIntl = useMemo(() => {
    switch(item.level) {
      case "ERROR": return intl.formatMessage({
        id: "alarms__level__error",
        defaultMessage: "Error",
      });
      case "WARNING": return intl.formatMessage({
        id: "alarms__level__warning",
        defaultMessage: "Warning",
      });
      case "INFO": return intl.formatMessage({
        id: "alarms__level__info",
        defaultMessage: "Info",
      });
      default: return item.level;
    }
  }, [intl, item.level]);

  return <>
    <Button
      title={`${itemLevelIntl} ${item.name}: ${item.message || "OK"}`}
      m={1}
      flexGrow={0}
      justifyContent="flex-start"
      colorScheme={lookActive || lookUnAcked ? itemLevelColor : undefined}
      opacity={lookMuted ? .4 : undefined}
      variant={lookUnAcked ? "outline" : "solid"}
      onClick={() => setDetailedType(item)}
    >
      <Icon
        as={itemLevelIcon[item.count ? 0 : 1]}
        color={!lookActive && !lookUnAcked ? `enposol.${colorTint}` : lookUnAcked ? `${itemLevelColor}.${colorTint}` : textColor}
        fontSize={lookActive ? "x-large" : "md"}
      />
      <Text fontSize={lookActive ? "x-large" : "md"} ml={2}>
        {item.abbr}
      </Text>
      <Text fontSize="sm" ml={2} textAlign="left" fontWeight="normal">
        {lookActive ? item.message : ""}
      </Text>
    </Button>
  </>
};

export default AlarmType;
